import { useEffect, useRef, useState } from "react";
import { faPrescription } from "@fortawesome/pro-regular-svg-icons";

import { parseNumber } from "common/utils/numbers";
import { ProductItemPriceBreak, SubscriptionFrequency } from "common/types";
import { DeliveryMethodKind, ItemFulfillmentOption } from "common/types/fulfillments";
import { useMatch, useNavigate, useSearchState } from "common/router/hooks";
import { pluralized, toHumanizedList } from "common/utils/strings";
import Icon from "common/components/Icon";
import { toDollars } from "common/utils/formatting";
import { StatusCode } from "common/fetch/types";
import { WithStringValues } from "common/utils/types";
import { Availability } from "common/types/catalog";

import { useFeatures } from "client/clinics/hooks";
import Page from "client/common/components/layouts/Page";
import Body from "client/common/components/layouts/Body";
import { ModalStatus } from "client/common/types";
import { useCartItemAddMutation } from "client/carts/hooks";
import { CartCode } from "client/carts/types";
import LoadingPage from "client/common/components/loading/LoadingPage";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import { Paths, urls } from "client/routes";
import { sendAddToCart, sendViewItem } from "client/analytics/utils";
import type { AnalyticsItemData } from "client/analytics/types";
import Banner from "client/common/components/content/Banner";
import { useBreakpoints } from "client/common/hooks";
import EmployeePricingNote from "client/common/components/content/EmployeePricingNote";
import FetchErrorPage from "client/common/components/errors/FetchErrorPage";

import ProductLineKlarnaAdvertisement from "./ProductLineKlarnaAdvertisement";
import NotFoundPage from "./NotFoundPage";
import ProductLineMedia from "./ProductLineMedia";
import ProductLineSelections from "./ProductLineSelections";
import {
  IProductItem,
  IProductLineDetails,
  IProductLineProduct,
  ProductDetailSearch,
  ProductLineTabKey,
} from "../types";
import ProductLinePrescriptions from "./ProductLinePrescriptions";
import {
  useProductLineAvailabilityQuery,
  useProductLineQuery,
  useProductQuery,
  useItemAddToCartPricing,
} from "../hooks";
import ProductLineAccordion from "./ProductLineAccordion";
import ProductLineInfo from "./ProductLineInfo";
import ProductLinePromotions from "./ProductLinePromotions";
import ProductLineWarnings from "./ProductLineWarnings";
import ProductLineContent from "./ProductLineContent";
import ProductLineReviews from "./ProductLineReviews";
import ProductLineSupport from "./ProductLineSupport";
import ProductLineOptionsBar from "./ProductLineOptionsBar";
import PreFooter from "./PreFooter";
import ProductLineDisclaimer from "./ProductLineDisclaimer";
import { useInvalidateProductLines } from "../hooks/invalidate";
import { getItemPriceFromQuantity } from "../utils";

import styles from "../styles/ProductLinePage.module.less";

type UnavailableProductLineAlertProps = {
  productLine: IProductLineDetails;
  search: WithStringValues<ProductDetailSearch>;
};

const UnavailableProductLineAlert = ({ productLine, search }: UnavailableProductLineAlertProps) => {
  const { data: availability, isLoading } = useProductLineAvailabilityQuery(productLine.id, {
    product: search.product,
    item: search.item,
  });

  if (isLoading) {
    return null;
  }

  const shippingAvailability = availability?.availability;

  return (
    <Banner variant="warning">
      <Banner.Content
        label={
          <span>
            {availability?.full_name ? <strong>{availability.full_name}</strong> : "This product"}
            {` ${
              shippingAvailability === Availability.Discontinued
                ? "is discontinued by the manufacturer. Browse the site or contact your veterinarian for guidance on an alternative product."
                : "is not available. Consider an alternative product"
            }`}
          </span>
        }
      />
    </Banner>
  );
};

const PriceBreak = ({
  priceBreak,
  isFinalPriceBreak,
}: {
  priceBreak: ProductItemPriceBreak;
  isFinalPriceBreak: boolean;
}) => {
  if (isFinalPriceBreak) {
    return (
      <div className={styles.priceBreak}>
        <div>{priceBreak.low_qty}+</div>
        <div>{toDollars(priceBreak.price)}/ea</div>
      </div>
    );
  }
  return (
    <div className={styles.priceBreak}>
      {priceBreak.low_qty === priceBreak.high_qty ? (
        <div>{priceBreak.low_qty}</div>
      ) : (
        <div>
          {priceBreak.low_qty}-{priceBreak.high_qty}
        </div>
      )}
      <div>{toDollars(priceBreak.price)}/ea</div>
    </div>
  );
};

const PriceBreakChart = ({ priceBreaks }: { priceBreaks: ProductItemPriceBreak[] }) => {
  return priceBreaks.length > 1 ? (
    <div className={styles.priceBreakContainer}>
      <div className={styles.priceBreakHeader}>
        <div>Quantity</div>
        <div>Unit Price</div>
      </div>
      <div className={styles.priceBreaks}>
        {priceBreaks.map((priceBreak: ProductItemPriceBreak, index: number) => (
          <PriceBreak
            key={priceBreak.low_qty}
            priceBreak={priceBreak}
            isFinalPriceBreak={index === priceBreaks.length - 1}
          />
        ))}
      </div>
    </div>
  ) : null;
};

type ProductLineDetailsProps = { productLine: IProductLineDetails };

const ProductLinePageContent = ({ productLine }: ProductLineDetailsProps) => {
  const selectionRef = useRef<HTMLDivElement>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const isFirstProductLoad = useRef(true);
  const { isPhoneAndSmaller } = useBreakpoints();
  const navigate = useNavigate();
  const features = useFeatures();

  const invalidateProductLines = useInvalidateProductLines();

  const [search, setSearch] = useSearchState<Paths["products"]["detail"]>({
    tab: ProductLineTabKey.Overview,
    modal: ModalStatus.Closed,
    product: "",
    item: "",
  });

  const productId = parseNumber(search.product, 0);
  const itemId = parseNumber(search.item, 0);

  // Resolve product from productLine data only (not productQuery), used for ProductSelect
  // so it remains stable and doesn't re-render when productQuery loads
  const previewProduct = (() => {
    if (!productLine.products.length) {
      return null;
    }
    const productBasedOnQuery = productId && productLine.products.find((p) => p.id === productId);
    const productBasedOnItem =
      itemId && productLine.products.find((p) => p.items.find((i) => i.id === itemId));
    return productBasedOnQuery || productBasedOnItem || productLine.products[0];
  })();

  // Use productId from URL, or resolve from itemId, or fall back to first product in productLine
  const productIdFromItem =
    itemId && productLine.products.find((p) => p.items.find((i) => i.id === itemId))?.id;
  const effectiveProductId = productId || productIdFromItem || productLine.products[0]?.id || 0;

  const productQuery = useProductQuery(effectiveProductId, { enabled: !!effectiveProductId });

  const resolveProduct = () => {
    if (!productLine.products.length || productQuery.isLoading) {
      return null;
    }
    return productQuery.data ?? null;
  };

  const product = resolveProduct();

  const nonSingleDispenseItems = product?.items.filter((i) => !i.is_single_dispense);
  const defaultNonSingleDispenseItem = nonSingleDispenseItems?.length
    ? nonSingleDispenseItems[0]
    : null;
  const defaultItem = product?.items.length ? product.items[0] : null;
  const item =
    (itemId
      ? product?.items.find((i) => i.id === itemId)
      : (defaultNonSingleDispenseItem ?? defaultItem)) || null;

  // Keep track of the last truthy item for the image carousel, so images don't flash
  // to a default/fallback state while productQuery is loading
  const [lastRenderedItem, setLastRenderedItem] = useState<IProductItem | null>(null);

  useEffect(() => {
    if (item) {
      setLastRenderedItem(item);
    }
  }, [item]);

  const [quantity, setQuantity] = useState(item?.default_quantity ?? 1);
  const [subscription, setSubscription] = useState<SubscriptionFrequency | null>(
    item?.recommended_frequency ?? null,
  );
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethodKind | null>(
    item?.default_delivery_method ?? null,
  );
  const [flavorSelection, setFlavorSelection] = useState(item?.default_flavor_selection);

  /** Builds the standard AnalyticsItemData from PDP's separate variables. */
  const toAnalyticsItemData = (
    pdpItem: IProductItem,
    pdpProduct: IProductLineProduct,
  ): AnalyticsItemData => ({
    ...pdpItem,
    product: { ...pdpProduct, product_line: productLine },
  });

  useEffect(() => {
    if (!product || !item) {
      return;
    }
    sendViewItem(toAnalyticsItemData(item, product), {
      price: item.non_subscription_discounted_price,
    });
    // We want to fire any time the product or item changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, item?.id]);

  const addSelections = product &&
    item && {
      product: product.id,
      item,
      quantity,
      subscription,
      flavor_selection: flavorSelection,
      fulfillment: deliveryMethod,
    };

  // We may want to add a toast on error here.
  const {
    mutate: addCartItem,
    isLoading,
    error,
  } = useCartItemAddMutation({
    shouldOpenCart: true,
    onSuccess: (_, cartItemQuantity) => {
      if (!addSelections) {
        return;
      }
      const price = addSelections?.subscription
        ? addSelections.item.subscription_discounted_price
        : addSelections.item.non_subscription_discounted_price;
      const discount = addSelections?.subscription
        ? addSelections.item.non_subscription_discounted_price -
          addSelections.item.subscription_discounted_price
        : 0;

      sendAddToCart({
        item: toAnalyticsItemData(addSelections.item, product),
        quantity: cartItemQuantity,
        price,
        discount,
      });
    },
  });

  const addToCart = () => {
    if (!addSelections) {
      return;
    }
    addCartItem({
      flavor_selection: flavorSelection || undefined,
      item: addSelections.item.id,
      quantity: addSelections.quantity,
      subscription: addSelections?.subscription || undefined,
      cart_id: CartCode.ACTIVE,
      is_pickup: addSelections.fulfillment === DeliveryMethodKind.Pickup,
    });
  };

  const setItemRelatedState = (
    nextItem: IProductItem,
    { isFirstLoad = false }: { isFirstLoad?: boolean } = {},
  ) => {
    const nextFulfillment = nextItem.fulfillment_options.find(
      (o: ItemFulfillmentOption) => o.kind === deliveryMethod,
    );
    setFlavorSelection(nextItem.flavor_options[0]?.options[0]?.id);
    setSubscription(isFirstLoad || subscription ? nextItem.recommended_frequency : null);
    setQuantity(nextItem.default_quantity);
    setDeliveryMethod(
      (nextFulfillment ? deliveryMethod : nextItem.default_delivery_method) ?? null,
    );
  };

  const setProduct = (id: number) => {
    const nextProduct = productLine.products.find((p) => p.id === id);
    const nextItemIndex = // Keep the perceived selected item the same
      nextProduct?.items.length === product?.items.length &&
      product?.items.map((i) => i.id).indexOf(addSelections?.item?.id ?? -1);
    const nextItem = nextProduct?.items[nextItemIndex || 0];

    if (!nextProduct || !nextItem) {
      return;
    }

    setSearch({
      product: nextProduct.id,
      item: nextItem.id,
    });
  };

  const setItem = (id: number) => {
    const nextItem = product?.items.find((current) => current.id === id);
    if (!product || !nextItem) {
      return;
    }
    setSearch({
      product: product.id,
      item: nextItem.id,
    });
  };

  useEffect(() => {
    setSubscription((s) => (item?.is_subscribable ? s : null));
  }, [item?.is_subscribable]);

  // Set item defaults via useEffect when item changes
  useEffect(() => {
    if (!item) {
      return;
    }
    setItemRelatedState(item, { isFirstLoad: isFirstProductLoad.current });
    isFirstProductLoad.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  const addButtonDisabled = !addSelections?.quantity || !item?.is_available;

  const fulfillment = item?.fulfillment_options.find((o) => o.kind === deliveryMethod);
  const prescriptions = productLine.products.flatMap((p) =>
    p.items.flatMap((i) => i.active_prescriptions),
  );

  // Wait for the item-driven `subscription` state to settle before querying;
  // otherwise the initial `null` subscription fires one request, the effect at
  // [item?.id] then sets it to `recommended_frequency`, and we fire a second.
  const isItemStateInitialized = !isFirstProductLoad.current;

  const pricingQuery = useItemAddToCartPricing(
    item?.id ?? 0,
    addSelections?.quantity ?? 1,
    !!subscription,
    { enabled: isItemStateInitialized },
  );

  const isPricingLoading = pricingQuery.isLoading;

  const feSideSubtotal =
    (addSelections?.quantity ?? 0) *
    (item && addSelections
      ? (getItemPriceFromQuantity(item, addSelections.quantity, subscription, item.price_breaks) ??
        0)
      : 0);
  const discountedSubtotal = pricingQuery.data?.discounted_subtotal ?? feSideSubtotal;

  const isProductLineGenerallyAvailable = productLine.products.length >= 1;

  if (productQuery.isLoading && isFirstProductLoad.current) {
    return <LoadingPage />;
  }

  // breaking out form for adding to cart into its own function for now to give some basic organization
  // to the html below. This would probably be easier to follow if this was in it's own component managing form state
  const renderAddToCartForm = () => {
    return (
      <>
        {product?.items && (
          <>
            <ProductLineSelections.ItemSelect
              productLine={productLine}
              items={product.items}
              value={item}
              onChange={(i) => setItem(i.id)}
              subscription={subscription}
              areFeesIncludedInSingleDispense={features.SINGLE_DISPENSE_FEE_INCLUSION}
            />
            {item?.flavor_options.map((optionsGroup) => (
              <ProductLineSelections.OptionSelect
                key={optionsGroup.label}
                optionsGroup={optionsGroup}
                onChange={setFlavorSelection}
                value={flavorSelection}
              />
            ))}
          </>
        )}
        {item && (
          <>
            {item.is_available ? (
              <>
                {item.fulfillment_options.length > 1 && deliveryMethod && (
                  <ProductLineSelections.DeliveryMethodSelect
                    value={deliveryMethod}
                    onChange={setDeliveryMethod}
                    fulfillmentOptions={item.fulfillment_options}
                    showUnavailableOptions={true}
                    messages={fulfillment?.messages ?? []}
                  />
                )}
                {fulfillment?.is_available && addSelections ? (
                  <>
                    <ProductLineSelections.SubscriptionSelect
                      value={subscription}
                      onChange={setSubscription}
                      item={item}
                      quantity={addSelections.quantity}
                    />
                    {fulfillment.is_low_stock && <ProductLineSelections.LowStock />}
                    <div className={styles.addSectionContainer}>
                      {productLine.should_show_employee_pricing_note && (
                        <EmployeePricingNote isMapPriceAdjusted={item.is_map_price_adjusted} />
                      )}
                      <ProductLineSelections.AddSection error={error}>
                        <ProductLineSelections.QuantitySelect
                          value={addSelections.quantity}
                          onChange={setQuantity}
                        />
                        <ProductLineSelections.AddToCartButton
                          onSubmit={addToCart}
                          disabled={addButtonDisabled || isPricingLoading}
                          isLoading={isLoading || isPricingLoading}
                          discountedSubtotal={discountedSubtotal}
                        />
                      </ProductLineSelections.AddSection>
                      {item.is_single_dispense && features.SINGLE_DISPENSE_FEE_INCLUSION && (
                        <div className={styles.singleUnitDispenseWarning}>
                          Price depends on quantity selected.
                        </div>
                      )}
                      {item.is_klarna_eligible && (
                        <ProductLineKlarnaAdvertisement discountedSubtotal={discountedSubtotal} />
                      )}
                    </div>
                    {isPhoneAndSmaller && <ProductLinePrescriptions productLine={productLine} />}
                  </>
                ) : (
                  <ProductLineSelections.Unavailable
                    item={item}
                    deliveryMethod={deliveryMethod}
                    onNotificationChange={invalidateProductLines}
                  />
                )}
              </>
            ) : (
              <ProductLineSelections.Unavailable
                item={item}
                deliveryMethod={deliveryMethod}
                onNotificationChange={invalidateProductLines}
              />
            )}
          </>
        )}
      </>
    );
  };

  return (
    <Page>
      <Body>
        <div className={styles.main}>
          {!!(
            isPhoneAndSmaller &&
            productLine.prescription_required &&
            productLine.doctor &&
            prescriptions.length
          ) && (
            <Banner.LinkButton
              variant="info"
              onClick={() => navigate(urls.prescriptions.list())}
              className={styles.prescriptionsBanner}
            >
              <div className={styles.rxBannerContent}>
                <Icon icon={faPrescription} />
                <Banner.Text
                  label={`${pluralized(
                    prescriptions.length,
                    "Prescription",
                  )} on file for ${toHumanizedList(
                    new Set(prescriptions.map((prescription) => prescription.patient.name_display)),
                  )}`}
                />
              </div>
            </Banner.LinkButton>
          )}
          <ProductLineMedia
            className={styles.images}
            productLine={productLine}
            search={search}
            item={item ?? lastRenderedItem}
            isAvailable={isProductLineGenerallyAvailable}
          />
          <div className={styles.details}>
            <ProductLineInfo
              productLine={productLine}
              onReviewClick={() => reviewsRef.current?.scrollIntoView({ behavior: "smooth" })}
            />
            {!isPhoneAndSmaller && <ProductLinePrescriptions productLine={productLine} />}
            <ProductLinePromotions item={item ?? lastRenderedItem} />
            <ProductLineWarnings item={item ?? lastRenderedItem} />
            {isProductLineGenerallyAvailable && (
              <ProductLineSelections.ProductSelect
                selectionRef={selectionRef}
                products={productLine.products}
                productLine={productLine}
                value={previewProduct}
                onChange={(p) => setProduct(p.id)}
              />
            )}
            {productQuery.isLoading ? (
              <LoadingContainer />
            ) : (
              <>
                {(!product || !item) && (
                  <UnavailableProductLineAlert productLine={productLine} search={search} />
                )}
                {isProductLineGenerallyAvailable && renderAddToCartForm()}
              </>
            )}
            <ProductLineAccordion productLine={productLine} />
            {!productQuery.isLoading && item && <PriceBreakChart priceBreaks={item.price_breaks} />}
            <ProductLineDisclaimer />
          </div>
        </div>
      </Body>
      {/* Body containers are rendered in these components for full width displays */}
      <div className={styles.secondary}>
        <ProductLineContent productLine={productLine} />
        <ProductLineSupport productLine={productLine} />
        <ProductLineReviews productLine={productLine} ref={reviewsRef} />
        <ProductLineOptionsBar selectionRef={selectionRef} productLine={productLine} />
        <PreFooter image={productLine.banner_image} />
      </div>
    </Page>
  );
};

const ProductLinePage = () => {
  const match = useMatch<Paths["products"]["detail"]>();
  const id = parseNumber(match.params.id, 0);
  const { data: productLine, isLoading, error } = useProductLineQuery(id);

  if (isLoading) {
    return <LoadingPage />;
  }

  if (error?.status === StatusCode.NotFound) {
    return <NotFoundPage />;
  }

  if (error || !productLine) {
    return <FetchErrorPage error={error} entityName="Product" />;
  }

  return <ProductLinePageContent key={id} productLine={productLine} />;
};

export default ProductLinePage;
